import { DAILY_LEARN_HARD_CAP } from '../utils/constants';
import {
  doc,
  runTransaction, // Import runTransaction
  Timestamp,
  arrayUnion,
  increment,
} from 'firebase/firestore';
import moment from 'moment-timezone';
import { nextReviewDateForStage } from '../utils/srs';
import { firestore } from '../firebaseConfig'; // Firestore setup

/**
 * Adds a sentence to the user's learning list AND sets its initial SRS state (Stage 1)
 * AND increments the daily learned count, all within a single atomic transaction.
 *
 * @param {string} userId - The ID of the user.
 * @param {string} sentenceId - The ID of the sentence to add and learn.
 * @returns {Promise<{status: string, sentenceId: string}>} - Success status.
 * @throws {Error} - Throws errors if user/sentence not found, subscription fails, or transaction fails.
 */
export const addSentenceToLearned = async (userId, sentenceId, isSubscribed, isExtraStudy = false, audioDuration = 0) => {
  const functionStartTime = Date.now();

  // Define references
  const userRef = doc(firestore, 'users', userId);
  const userSentenceDocRef = doc(firestore, 'users', userId, 'user_sentences', sentenceId);
  const mainSentenceDataRef = doc(firestore, 'sentences', sentenceId);
  const userSentencesIdsRef = doc(firestore, 'users', userId, 'metadata', 'user_sentences_ids');
  const learnedTodayRef = doc(firestore, 'users', userId, 'metadata', 'learned_today');

  try {
    const alreadyLearned = await runTransaction(firestore, async (transaction) => {
      // 1. Read all required documents
      const userSnapshot = await transaction.get(userRef);
      const sentenceSnapshot = await transaction.get(mainSentenceDataRef);
      const userSentenceSnapshot = await transaction.get(userSentenceDocRef);
      const learnedTodaySnapshot = await transaction.get(learnedTodayRef);

      if (!userSnapshot.exists()) {
        throw new Error(`User ${userId} not found`);
      }
      if (!sentenceSnapshot.exists()) {
        console.error(`Sentence ${sentenceId} not found in the main 'sentences' collection.`);
        const notFoundError = new Error(`Sentence ${sentenceId} not found.`);
        notFoundError.code = 'sentence-not-found';
        throw notFoundError;
      }

      // Guard: sentence already learned — do not double-count metadata
      if (userSentenceSnapshot.exists()) return true;

      const userData = userSnapshot.data();
      const sentenceData = sentenceSnapshot.data();
      const sentenceLevel = sentenceData.level;
      const sentenceWkLevel = sentenceData.kanji_complexity;

      // 2. Subscription check
      if (!isSubscribed && sentenceLevel > 0 && sentenceLevel < 5) {
        const error = new Error(`Premium required for Level ${sentenceLevel} sentence.`);
        error.code = 'premium-required';
        error.level = sentenceLevel;
        throw error;
      }

      // 3. Calculate date and SRS details
      const userTimezone = userData.settings?.timezone || 'UTC';
      const todayDate = moment().tz(userTimezone).format('YYYY-MM-DD');

      // Guard: vacation mode
      if (userData.settings?.vacation_started_at) {
        const err = new Error('Cannot learn new sentences while vacation mode is active.');
        err.code = 'vacation-mode';
        throw err;
      }

      // Guard: daily learn limit (concurrent sessions could both bypass the fetch-time check)
      const dailyLimit = isExtraStudy ? DAILY_LEARN_HARD_CAP : Math.min(userData.settings?.sentences_per_day || DAILY_LEARN_HARD_CAP, DAILY_LEARN_HARD_CAP);
      const learnedTodayData = learnedTodaySnapshot.exists() ? learnedTodaySnapshot.data() : {};
      const learnedCount = learnedTodayData?.data?.[todayDate] || 0;
      if (learnedCount >= dailyLimit) {
        const err = new Error('Daily learning limit reached.');
        err.code = 'daily-limit-reached';
        throw err;
      }
      const nextReviewDateForStage1 = nextReviewDateForStage(1);
      const currentTimestamp = Timestamp.now();

      // 4. Define the new sentence data
      const newUserSentenceData = {
        sentence_ref: mainSentenceDataRef,
        srs_stage: 1,
        correct: 1,
        wrong: 0,
        learned_at: currentTimestamp,
        next_review: Timestamp.fromDate(nextReviewDateForStage1),
        skipped: false,
        level: sentenceLevel,
      };

      // 5. Perform all writes
      transaction.set(userSentenceDocRef, newUserSentenceData);
      transaction.set(userSentencesIdsRef, { data: arrayUnion(sentenceId) }, { merge: true });
      transaction.set(learnedTodayRef, { data: { [todayDate]: increment(1) } }, { merge: true });

      // Update metadata stats for the new learned sentence (starts at SRS stage 1)
      const levelRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_level');
      const srsRef = doc(firestore, 'users', userId, 'metadata', 'counts_by_srs');
      const accuracyRef = doc(firestore, 'users', userId, 'metadata', 'review_accuracy');
      
      const levelUpdate = {
        sentences_by_wk: {
          [sentenceLevel]: {
            [sentenceWkLevel]: increment(1)
          }
        }
      };
      transaction.set(levelRef, levelUpdate, { merge: true });
      
      const srsUpdate = {
        sentences: {
          1: increment(1) // New learned sentences start at SRS stage 1
        }
      };
      transaction.set(srsRef, srsUpdate, { merge: true });
      
      // Update accuracy stats since we're setting correct: 1 initially
      const accuracyUpdate = {
        sentences: {
          correct: increment(1)
        }
      };
      transaction.set(accuracyRef, accuracyUpdate, { merge: true });

      // Listening time: the learn session plays the sentence audio once, so count
      // it toward total listening seconds (mirrors the +1 the backfill derives
      // from this sentence's initial correct: 1).
      const listeningRef = doc(firestore, 'users', userId, 'metadata', 'listening_stats');
      transaction.set(listeningRef, { sentences: { seconds: increment(audioDuration) } }, { merge: true });
    });

    if (alreadyLearned) return { status: 'already_learned', sentenceId };
    return { status: 'fulfilled', sentenceId: sentenceId };
  } catch (error) {
    console.error(`[${functionStartTime}] Transaction FAILED:`, error);

    if (['premium-required', 'sentence-not-found', 'vacation-mode', 'daily-limit-reached'].includes(error.code)) {
      throw error;
    }
    if (error.code === 'permission-denied') {
      const permissionError = new Error('Permission denied during learning operation.');
      permissionError.code = 'permission-denied-learnsentence';
      throw permissionError;
    }
    throw new Error(`Failed to add learned sentence: ${error.message || error}`);
  }
};
